import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MapPin, Search, X } from 'lucide-react';

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchRow = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 0.93rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  box-sizing: border-box;
  transition: border-color 160ms;

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const LeftIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.muted};
  pointer-events: none;
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 160ms;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.large};
  max-height: 280px;
  overflow-y: auto;
  z-index: 20;
`;

const Result = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 10px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background 160ms;

  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`}; }
`;

const ResultIcon = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  margin-top: 2px;
  flex-shrink: 0;
`;

const ResultText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const Selected = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 8px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SelectedMain = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SelectedSub = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Hint = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 8px 12px;
`;

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

const pickCity = (address = {}) =>
  address.city ||
  address.town ||
  address.village ||
  address.municipality ||
  address.county ||
  address.state ||
  '';

const LocationPicker = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const hasValue = value?.lat != null && value?.lng != null;

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${NOMINATIM_URL}?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (err) {
        console.error('LocationPicker search failed', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handlePick = (r) => {
    const city = pickCity(r.address);
    const country = r.address?.country || '';
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    onChange({ city, country, lat, lng });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange({ city: '', country: '', lat: '', lng: '' });
    setQuery('');
    setResults([]);
  };

  return (
    <Wrap ref={wrapRef}>
      <SearchRow>
        <LeftIcon><Search size={16} /></LeftIcon>
        <SearchInput
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={hasValue ? 'Search to change location…' : 'Search a city or place — e.g., Toronto, Canada'}
        />
        {query && (
          <ClearBtn type="button" onClick={() => { setQuery(''); setResults([]); }} aria-label="Clear search">
            <X size={14} />
          </ClearBtn>
        )}
        {open && (results.length > 0 || loading) && (
          <Dropdown>
            {loading && <Hint>Searching…</Hint>}
            {results.map((r) => (
              <Result key={r.place_id} type="button" onClick={() => handlePick(r)}>
                <ResultIcon><MapPin size={14} /></ResultIcon>
                <ResultText>{r.display_name}</ResultText>
              </Result>
            ))}
          </Dropdown>
        )}
      </SearchRow>

      {hasValue && (
        <Selected>
          <MapPin size={16} color="#FF5B3A" />
          <div style={{ flex: 1 }}>
            <SelectedMain>
              {[value.city, value.country].filter(Boolean).join(', ') || 'Selected location'}
            </SelectedMain>
            <SelectedSub>
              {Number(value.lat).toFixed(4)}, {Number(value.lng).toFixed(4)}
            </SelectedSub>
          </div>
          <ClearBtn type="button" onClick={handleClear} aria-label="Clear location">
            <X size={16} />
          </ClearBtn>
        </Selected>
      )}
    </Wrap>
  );
};

export default LocationPicker;
